import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  PeopleOutline as ClientsIcon,
  PersonAddAlt1Outlined as NewClientIcon,
  PhoneOutlined as PhoneIcon,
  RestoreFromTrash as RestoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import PermissionButton from '../../../components/PermissionButton';
import CustomTablePagination from '../../../components/CustomTablePagination';
import useAlert from '../../../hooks/useAlert';
import { usePermissions } from '../../../contexts/PermissionContext';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { comptoirAPI } from '../../../services/api';
import { useComptoirClients, useComptoirDeletedClients, useInvalidateComptoir } from '../../../hooks/queries/useComptoir';
import { ComptoirClient } from '../../../types/comptoir';
import { COLOR, ClientAvatar, EmptyState, dh, digits, fmtPhone, num, primaryBtnSx } from '../ui';
import ClientFormDialog from '../components/ClientFormDialog';

const MINI = 'Clients';

const ComptoirClients: React.FC = () => {
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission('Comptoir', 'add', MINI);
  const invalidate = useInvalidateComptoir();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ComptoirClient | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const listQuery = useComptoirClients({ skip: page * rowsPerPage, limit: rowsPerPage, search: debounced || undefined }, tab === 0);
  const deletedQuery = useComptoirDeletedClients(tab === 1);
  const clients = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;

  const openDialog = (c: ComptoirClient | null) => { setEditing(c); setDialogOpen(true); };

  // Same digits-only rule as the server
  const findDuplicate = async (tel: string) => {
    const d = digits(tel);
    const r = await comptoirAPI.getClients({ search: d, limit: 20 });
    return r.items.find((c) => digits(c.telephone) === d) || null;
  };

  const handleDelete = (c: ComptoirClient) => {
    showConfirm(`« ${c.nom} » ira dans la corbeille. Vous pourrez le restaurer.`, async () => {
      try {
        await comptoirAPI.deleteClient(c.id);
        showSuccess(`Client ${c.nom} supprimé`);
        invalidate();
      } catch (err: any) {
        showError(extractErrorMessage(err));
      }
    }, { title: 'Supprimer ce client ?', confirmText: 'Supprimer' });
  };

  const handleRestore = async (c: ComptoirClient) => {
    try {
      await comptoirAPI.restoreClient(c.id);
      showSuccess(`Client ${c.nom} restauré`);
      invalidate();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const nameCell = (c: ComptoirClient) => (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <ClientAvatar name={c.nom} size={36} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.nom}</Typography>
        {c.surnom && <Typography variant="caption" color="text.secondary">« {c.surnom} »</Typography>}
      </Box>
    </Stack>
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR }}>Clients</Typography>
          <Typography variant="body2" color="text.secondary">
            Les clients du comptoir. Le téléphone sert à retrouver un client : un numéro n'appartient qu'à un seul client.
          </Typography>
        </Box>
        <PermissionButton
          moduleName="Comptoir" operation="add" miniModuleName={MINI} variant="contained" size="large"
          startIcon={<NewClientIcon />} onClick={() => openDialog(null)} sx={{ ...primaryBtnSx, whiteSpace: 'nowrap', px: 3 }}
        >
          Nouveau client
        </PermissionButton>
      </Stack>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2} sx={{ px: 2, pt: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} TabIndicatorProps={{ sx: { bgcolor: COLOR } }}
            sx={{ '& .Mui-selected': { color: `${COLOR} !important` } }}>
            <Tab label={`Clients (${total})`} />
            <Tab label="Corbeille" />
          </Tabs>
          {tab === 0 && (
            <TextField
              size="small" placeholder="Nom, surnom ou téléphone…" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: { md: 340 }, pb: { xs: 1, md: 0 } }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} aria-label="Effacer la recherche"><ClearIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Stack>
        {listQuery.isFetching && tab === 0 && <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { bgcolor: COLOR }, bgcolor: alpha(COLOR, 0.1) }} />}

        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            {!listQuery.isLoading && clients.length === 0 ? (
              debounced ? (
                <EmptyState icon={<SearchIcon />} title="Aucun client trouvé" text={`Rien ne correspond à « ${debounced} ».`}
                  action={{ label: 'Effacer la recherche', onClick: () => setSearch('') }} />
              ) : (
                <EmptyState icon={<ClientsIcon />} title="Aucun client pour l'instant"
                  text="Les clients se créent ici ou directement depuis une nouvelle demande."
                  action={canAdd ? { label: 'Nouveau client', icon: <AddIcon />, onClick: () => openDialog(null) } : undefined} />
              )
            ) : (
              <>
                <TableContainer sx={getModuleTableTheme('comptoir')}>
                  <Table size="small" sx={{ minWidth: 980 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell>Téléphone</TableCell>
                        <TableCell align="center">Offres</TableCell>
                        <TableCell align="center">Services</TableCell>
                        <TableCell align="right">Total commandé</TableCell>
                        <TableCell align="right">Encaissé</TableCell>
                        <TableCell align="right">Solde</TableCell>
                        <TableCell align="center" width={110}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listQuery.isLoading
                        ? Array.from({ length: 5 }).map((_, i) => (
                          <TableRow key={i}>{Array.from({ length: 8 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                        ))
                        : clients.map((c) => (
                          <TableRow key={c.id} hover onClick={() => navigate(`/comptoir/clients/${c.id}`)} sx={{ cursor: 'pointer' }}>
                            <TableCell>{nameCell(c)}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{fmtPhone(c.telephone)}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={c.offres_count ?? 0} size="small"
                                sx={(c.offres_count ?? 0) > 0 ? { bgcolor: alpha(COLOR, 0.12), color: COLOR, fontWeight: 700 } : {}} />
                            </TableCell>
                            <TableCell align="center">{c.services || '—'}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{dh(c.total)}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'success.main' }}>{dh(c.paye)}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', fontWeight: 700, color: num(c.reste) > 0 ? 'error.main' : 'text.secondary' }}>{dh(c.reste)}</TableCell>
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                              <PermissionButton moduleName="Comptoir" operation="edit" miniModuleName={MINI} component="IconButton" size="small" tooltipTitle="Modifier" onClick={() => openDialog(c)}>
                                <EditIcon fontSize="small" />
                              </PermissionButton>
                              {(c.offres_count ?? 0) > 0 ? (
                                <Tooltip title={`Ce client a ${c.offres_count} offre(s) : supprimez-les d'abord`}>
                                  <span><IconButton size="small" disabled><DeleteIcon fontSize="small" /></IconButton></span>
                                </Tooltip>
                              ) : (
                                <PermissionButton moduleName="Comptoir" operation="delete" miniModuleName={MINI} component="IconButton" size="small" color="error" tooltipTitle="Supprimer" onClick={() => handleDelete(c)}>
                                  <DeleteIcon fontSize="small" />
                                </PermissionButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <CustomTablePagination
                  count={total} page={page} rowsPerPage={rowsPerPage}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
              </>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            {(deletedQuery.data ?? []).length === 0 && !deletedQuery.isLoading ? (
              <EmptyState icon={<DeleteIcon />} title="La corbeille est vide" text="Les clients supprimés apparaissent ici et peuvent être restaurés." />
            ) : (
              <TableContainer sx={getModuleTableTheme('comptoir')}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell>Téléphone</TableCell>
                      <TableCell>Supprimé le</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(deletedQuery.data ?? []).map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{nameCell(c)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{fmtPhone(c.telephone)}</TableCell>
                        <TableCell>{c.deleted_at ? new Date(c.deleted_at).toLocaleString('fr-FR') : '—'}</TableCell>
                        <TableCell align="center">
                          <PermissionButton moduleName="Comptoir" operation="edit" miniModuleName={MINI} size="small" startIcon={<RestoreIcon />} onClick={() => handleRestore(c)} sx={{ color: COLOR }}>
                            Restaurer
                          </PermissionButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>


      <ClientFormDialog
        open={dialogOpen}
        client={editing}
        onClose={() => setDialogOpen(false)}
        save={(data) => (editing ? comptoirAPI.updateClient(editing.id, data) : comptoirAPI.createClient(data))}
        findDuplicate={findDuplicate}
        onSaved={(c) => {
          setDialogOpen(false);
          invalidate();
          showSuccess(editing ? `Fiche de ${c.nom} enregistrée` : `Client ${c.nom} créé`);
        }}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default ComptoirClients;
